/**
 * 견적 금액 계산 — 견적서 금액의 **유일한 정본**.
 *
 * 순수 함수만 둔다(React·Orval·부수효과 없음). vendor 작성 화면이 쓰던 계산기를 그대로
 * 올린 것이고, 이제 서버 저장·PDF 생성·채팅 견적 모달이 **같은 함수**를 쓴다.
 *
 * **왜 옮겼나** — 종전에는 소비자 셋이 각자 다시 계산했고 서로 달랐다:
 * - 서버 저장(`quote-response.entity.ts`)은 `taxType` 을 무시하고 항상 부가세를 가산
 * - PDF(`pdf-data-transform.service.ts`)는 총액을 `/1.1` 로 역산
 * - 채팅 견적 모달(`EditQuoteModal`)도 자체 `/1.1`
 * 그래서 «미리보기와 실제 견적서가 다르다» 가 났다(실측: 합계가 정확히 10% 차이).
 * 계산이 한 곳에 있어야 그 어긋남이 구조적으로 불가능해진다.
 *
 * @module quote-amounts
 */

export interface QuoteItem {
  itemName: string;
  quantity: number;
  unitPrice: number;
  unit?: string;
  itemId?: string;
  updateCatalog?: number; // 0 or 1
  specification?: string;
}

/**
 * Calculate total price for a single quote item
 * @param item - Quote item with quantity and unit price
 * @returns Total price (quantity × unitPrice)
 */
export const calculateItemTotal = (item: QuoteItem): number => {
  return item.quantity * item.unitPrice;
};

/**
 * Calculate subtotal for all quote items (before VAT)
 * @param items - Array of quote items
 * @returns Subtotal amount
 */
export const calculateQuoteSubtotal = (items: QuoteItem[]): number => {
  return items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
};

/**
 * 단가를 어느 축으로 입력했는지 — DB `quote_response_versions.tax_type` 과 같은 값.
 *
 * - `EXCLUSIVE` 부가세 별도: 입력 단가가 **공급가액**. 부가세를 위에 가산한다.
 * - `INCLUSIVE` 부가세 포함: 입력 단가가 **부가세 포함가**. 합계에서 공급가액을 역산한다.
 */
export type QuoteVatMode = "EXCLUSIVE" | "INCLUSIVE";

/** 기본값 — 종전 동작(부가세 별도 단가)을 유지한다. */
export const DEFAULT_VAT_MODE: QuoteVatMode = "EXCLUSIVE";

/** 부가가치세율 10%. */
const VAT_RATE = 0.1;

/** 견적 금액 3분해 — 세 값 모두 원 단위 정수이고 `공급가액 + 부가세 = 총액`. */
export interface QuoteAmounts {
  /** 공급가액 (부가세 별도) */
  supplyAmount: number;
  /** 부가세 */
  vatAmount: number;
  /** 부가세 포함 총액 */
  totalAmount: number;
}

/**
 * Calculate VAT amount based on subtotal (공급가액 기준)
 *
 * 공급가액 × 세율을 **소수점 첫째자리에서 사사오입**한다(원 단위 정수).
 * 종전에는 절사(`Math.floor`)라 공급가액 끝자리가 5~9면 부가세가 1원 적게 잡혔다.
 *
 * @param subtotal - Subtotal amount before VAT (공급가액)
 * @param vatRate - VAT rate (default: 0.1 = 10%)
 * @returns VAT amount
 */
export const calculateVAT = (subtotal: number, vatRate = VAT_RATE): number => {
  return Math.round(subtotal * vatRate);
};

/**
 * Calculate grand total including VAT
 * @param subtotal - Subtotal amount before VAT (공급가액)
 * @param vatRate - VAT rate (default: 0.1 = 10%)
 * @returns Grand total (subtotal + VAT)
 */
export const calculateGrandTotal = (
  subtotal: number,
  vatRate = VAT_RATE,
): number => {
  return subtotal + calculateVAT(subtotal, vatRate);
};

/**
 * 부가세 포함 총액 → 공급가액 (사사오입).
 *
 * 나눗셈이라 절사를 쓰면 안 된다 — `1100 / 1.1 === 999.9999999999999` 이라
 * `Math.floor` 는 999 를 낸다.
 */
export const calculateSupplyFromTotal = (totalAmount: number): number => {
  return Math.round(totalAmount / (1 + VAT_RATE));
};

/**
 * 품목 합계를 입력 축(`mode`)에 따라 공급가액·부가세·총액으로 분해한다.
 *
 * **왜 모드가 필요한가**: 부가세 포함 16,000원에 거래하는 공급사가 공급가액 단가를 직접
 * 계산해 넣으면(16,000 ÷ 1.1 = 14,545.45 → 14,545) 버려진 0.45원이 수량만큼 증폭돼 총액이
 * 어긋난다(실측: 단가 14,545 × 수량 116 → 총액 1,855,942 vs 실제 1,856,000, 58원 부족).
 * 포함가로 입력받아 합계에서 역산하면 이 오차가 생기지 않는다.
 *
 * - `EXCLUSIVE`: 공급가액 = 품목 합계 → 부가세 = round(공급가액 × 10%) → 총액 = 합
 * - `INCLUSIVE`: 총액 = 품목 합계 → 공급가액 = round(총액 / 1.1) → 부가세 = 총액 − 공급가액
 */
export const calculateQuoteAmounts = (
  items: QuoteItem[],
  mode: QuoteVatMode = DEFAULT_VAT_MODE,
): QuoteAmounts => {
  const itemsTotal = calculateQuoteSubtotal(items);

  if (mode === "INCLUSIVE") {
    const totalAmount = Math.round(itemsTotal);
    const supplyAmount = calculateSupplyFromTotal(totalAmount);
    return { supplyAmount, vatAmount: totalAmount - supplyAmount, totalAmount };
  }

  const supplyAmount = Math.round(itemsTotal);
  const vatAmount = calculateVAT(supplyAmount);
  return { supplyAmount, vatAmount, totalAmount: supplyAmount + vatAmount };
};

// 금액 표시는 `formatters.ts` 의 `formatCurrencyWithSymbol` 을 쓴다 — 여기 있던 같은 이름의
// 함수는 그것과 동작이 같은 중복이었고, 공유 패키지로 올리면서 export 이름이 충돌했다.
